import React, { useState, useEffect, useRef } from 'react';
import './EPC.css';
import './custom-fixes.css';
import Footer from '../../components/Footer';
import NextPageCTA from '../../components/NextPageCTA';
import BannerLogo from '../../components/BannerLogo';

// Import Font Awesome icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHardHat, 
  faDraftingCompass, 
  faTruckLoading, 
  faWrench,
  faTools,
  faChartLine,
  faCheckCircle,
  faBriefcase,
  faSun,
  faGears,
  faHandshake
} from '@fortawesome/free-solid-svg-icons';

// Import banner image
import epcBanner from '../../assets/images/epc-banner.jpg'; // You'll need to add this image

// Add this new component for the content card
const ContentCard = ({ title, content, icon }) => (
  <div className="content-card">
    <div className="content-card-icon-wrapper">
      <div className="content-card-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
    </div>
    <div className="content-card-content">
      <h3>{title}</h3>
      <p>{content}</p>
    </div>
  </div>
);

const EPC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const overviewRef = useRef(null);

  // Function to check if element is in viewport
  const isElementInViewport = (el) => {
    if (!el) return false;
    
    const rect = el.getBoundingClientRect();
    return (
      rect.top < window.innerHeight * 0.8 &&
      rect.bottom >= 0
    );
  };

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      // Handle section visibility for animations
      if (overviewRef.current) {
        if (isElementInViewport(overviewRef.current)) {
          overviewRef.current.classList.add('visible');
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 300);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const solarContent = {
    introduction: {
      title: "Empowering Organisations and Institutions, Enabling a Greener Tomorrow",
      content: "At Vardaan Resources Pvt. Ltd., we bring our legacy of Engineering excellence into the realm of solar energy with a strong focus on large-scale implementation for industrial,  Institutional clients and Individuals. Our strategic partnerships with some of the most prominent players in the solar energy landscape have enabled us to deliver turnkey EPC solutions that are reliable, cost-efficient, and tailored to the operational scale of major organizations and government entities.",
      icon: faSun
    },
    capabilities: {
      title: "Comprehensive Service Offerings and Value Proposition",
      content: "Vardaan Resources Pvt Ltd, leveraging its engineering and project execution expertise, has partnered with a leading solar solutions provider to deliver end-to-end EPC services in the solar energy domain. Our offerings span the complete lifecycle of solar projects — from advisory, policy review, and framework management to installation, operations, and long-term maintenance. We specialize in setting up ground-mounted plants, rooftop systems, super structures, agro-photovoltaic models, and solar car park solutions. With a focus on quality, scalability, and compliance, we ensure that each project meets the highest industry standards. Our strength lies in integrating strategic planning with hands-on execution, enabling clients to harness solar energy with confidence. Through this collaboration, we bring speed, certainty, and sustainability to solar infrastructure development.",
      icon: faGears
    },
    additional: {
      title: "Our Commitment to Excellence",
      content: "Beyond infrastructure delivery, we also support policy formulation and advisory engagement with government bodies/authorities, empowering institutions to adopt solar energy through informed decision-making and structured implementation pathways. With every project, we bring together innovation, compliance, and execution rigor, enabling sustainable impact at scale. Our synergies with industry-leading solar partners allow us to draw from a pool of engineering depth, procurement agility, and technology optimization—ensuring that each installation is future-ready, grid-compliant, and operationally efficient. Whether you're a manufacturing conglomerate, a public sector entity, or a large-scale commercial establishment, Vardaan Resources is equipped to make your transition to solar seamless, strategic, and successful.",
      icon: faHandshake
    },
    expertise: {
      title: "Project Expertise",
      items: [
        "End-to-end EPC services for solar energy projects",
        "Advisory on solar policy and regulatory frameworks",
        "Feasibility studies and project planning",
        "Ground-mounted solar plant installations",
        "Rooftop and superstructure solar setups",
        "Agro-photovoltaic and car park solar solutions",
        "Operations and preventive maintenance services"
      ]
    }
    
  };

  return (
    <div className="epc-page">
      {/* EPC Hero Section */}
      <div className="epc-hero">
        <div className="epc-hero-logo-wrapper">
          <BannerLogo />
        </div>
        <img 
          src={epcBanner}
          alt="EPC Services Banner" 
          className="epc-hero-image" 
        />
        <div className="epc-hero-overlay"></div>
        <h1 className="epc-heading">Engineering, Procurement, and Construction - Solar</h1>
      </div>

      {/* EPC Overview Section */}
      <section className="epc-overview" ref={overviewRef}>
        <div className="decorative-dots decorative-dots-1"></div>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Solar EPC Expertise</h2>
          </div>

          <div className="content-cards-grid">
            <ContentCard 
              title={solarContent.introduction.title} 
              content={solarContent.introduction.content}
              icon={solarContent.introduction.icon}
            />
            <ContentCard 
              title={solarContent.capabilities.title} 
              content={solarContent.capabilities.content}
              icon={solarContent.capabilities.icon}
            />
            <ContentCard 
              title={solarContent.additional.title}
              content={solarContent.additional.content}
              icon={solarContent.additional.icon}
            />
          </div>
        </div>
        
        {/* Project Expertise Section - Moved inside the epc-overview section */}
        <div className="project-expertise-section">
          <div className="container">
            <div className="section-header project-expertise-header">
              <h2 className="section-title">Project Expertise</h2>
              <div className="title-underline"></div>
              <p className="section-subtitle">Our Project Capability Spans</p>
            </div>
            <div className="expertise-cards-grid">
              {solarContent.expertise.items.map((item, index) => (
                <div key={index} className="expertise-item-card">
                  <div className="expertise-icon">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Journey Navigation - Next Page CTA */}
      <NextPageCTA 
        title="Meet Our Management Team" 
        description="Get to know the experienced professionals behind our success, leading with expertise and vision across diverse engineering domains."
        nextPage={{
          path: "/management",
          label: "Meet Our Team"
        }}
        headingText="MEET US IN YOUR JOURNEY"
      />
      <Footer />
    </div>
  );
};

export default EPC; 